import { IAccount } from '@medely/types';
import { GET_CURRENT_USER } from '../graphql';
import { useQuery } from '@tanstack/react-query';
import { isSaasAccount, isMarketplaceAccount, isAgencyAccount } from '../utils/account';
import { useConfigurationContext } from '@medely/ui-kit';
import { GqlRequest, useNetworkRequest } from './useNetworkRequest';
import { useFirebaseContext } from './useFirebaseContext';

const DEFAULT_REFETCH_INTERVAL = 15000;

const getCurrentUser = async (gqlRequest: GqlRequest) => {
  const { me } = await gqlRequest(GET_CURRENT_USER);
  return me;
};

export const useCurrentUser = (
  refetch = false,
): {
  isLoading: boolean;
  currentUser: IAccount | undefined;
  isDeactivated: boolean;
  isApplicant: boolean;
  isRejected: boolean;
  isSaas: boolean;
  isMarketplace: boolean;
  isAgency: boolean;
  proType: 'irp' | 'agency' | 'marketplace';
} => {
  const { googleUser, logout } = useFirebaseContext();
  const { adminUrl, facilityUrl } = useConfigurationContext();
  const { gqlRequest } = useNetworkRequest();

  const enabled = !!googleUser && !window?.Cypress;

  const REDIRECT_URLS = {
    administrator: adminUrl,
    facility: facilityUrl,
  };

  const { isLoading, isInitialLoading, data } = useQuery(
    ['currentUser', googleUser?.email],
    (_queryContext) => getCurrentUser(gqlRequest),
    {
      enabled,
      onSuccess: (account: IAccount) => {
        if (account.account_type !== 'professional') {
          logout?.(REDIRECT_URLS[account.account_type as keyof typeof REDIRECT_URLS]);
        }
      },
      refetchInterval: refetch ? DEFAULT_REFETCH_INTERVAL : undefined,
    },
  );

  const isSaas = data ? isSaasAccount(data) : false;
  const isMarketplace = data ? isMarketplaceAccount(data) : false;
  const isAgency = data ? isAgencyAccount(data) : false;

  const getProType = () => {
    if (isSaas) {
      return 'irp';
    }
    if (isAgency) {
      return 'agency';
    }
    return 'marketplace';
  };

  return {
    isLoading: enabled ? isLoading : isInitialLoading,
    currentUser: data,
    isDeactivated: data?.professional?.status === 'deactivated' || false,
    isApplicant: data?.professional?.application_status !== 'approved',
    isRejected: data?.professional?.application_status === 'rejected',
    isMarketplace,
    isSaas,
    isAgency,
    proType: getProType(),
  };
};
